<template>
  <!-- Only show container if ad should be displayed AND (ad is loaded OR we're still loading) -->
  <div 
    v-if="shouldShowAd && (adLoaded || isLoading)" 
    :class="containerClasses"
    role="complementary"
    aria-label="Advertisement"
  >
    <!-- Loading state -->
    <div v-if="isLoading && !adLoaded" :class="loadingClasses">
      <div class="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
        <span class="text-xs text-gray-500 dark:text-gray-400">Loading ad...</span>
      </div>
    </div>

    <!-- Ad content (only show when loaded or after timeout) -->
    <div v-show="!isLoading || adLoaded">
      <!-- Square Ad (Auto format) -->
      <div v-if="adType === 'square'" ref="adContainer">
        <ins 
          class="adsbygoogle"
          style="display:block"
          :data-ad-client="adSenseConfig.getClientId()"
          :data-ad-slot="adSenseConfig.getAdSlot('square')"
          data-ad-format="auto"
          data-full-width-responsive="true"
        ></ins>
      </div>

      <!-- In-feed Ad (Auto relaxed) -->
      <div v-else-if="adType === 'infeed'" ref="adContainer">
        <ins 
          class="adsbygoogle"
          style="display:block"
          data-ad-format="autorelaxed"
          :data-ad-client="adSenseConfig.getClientId()"
          :data-ad-slot="adSenseConfig.getAdSlot('infeed')"
        ></ins>
      </div>

      <!-- Banner Ad (Responsive) -->
      <div v-else-if="adType === 'banner'" ref="adContainer">
        <ins 
          class="adsbygoogle"
          style="display:block"
          :data-ad-client="adSenseConfig.getClientId()"
          :data-ad-slot="adSenseConfig.getAdSlot('banner')"
          data-ad-format="auto"
          data-full-width-responsive="true"
        ></ins>
      </div>

      <!-- Rectangle Ad -->
      <div v-else-if="adType === 'rectangle'" ref="adContainer">
        <ins 
          class="adsbygoogle"
          style="display:block"
          :data-ad-client="adSenseConfig.getClientId()"
          :data-ad-slot="adSenseConfig.getAdSlot('rectangle')"
          data-ad-format="auto"
          data-full-width-responsive="true"
        ></ins>
      </div>
    </div>

    <!-- Ad Label -->
    <div v-if="showLabel && (adLoaded || !isLoading)" class="text-xs text-gray-400 text-center mt-2">
      Advertisement
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue';
import { useConsentStore } from '@/stores/consentStore';
import { adSenseConfig } from '@/services/adSenseConfig';

// Define emits
const emit = defineEmits<{
  'ad-loaded': [];
  'ad-failed': [];
}>();

interface Props {
  adType: 'square' | 'infeed' | 'banner' | 'rectangle';
  className?: string;
  showLabel?: boolean;
  minContentHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
  className: '',
  showLabel: true,
  minContentHeight: 300
});

// State
const adContainer = ref<HTMLElement | null>(null);
const adLoaded = ref(false);
const isLoading = ref(false);
const loadTimeout = ref<number | null>(null);

// Composables
const consentStore = useConsentStore();

// Computed
const shouldShowAd = computed(() => {
  // Only show ads if user has consented to advertising
  if (!consentStore.canUseAdvertising) {
    return false;
  }
  
  // Check if page has sufficient content
  const contentHeight = document.documentElement.scrollHeight;
  if (contentHeight < props.minContentHeight) {
    return false;
  }
  
  return true;
});

const containerClasses = computed(() => [
  'google-ad-container',
  props.className,
  {
    'ad-loaded': adLoaded.value,
    'ad-loading': isLoading.value
  }
]);

const loadingClasses = computed(() => {
  const baseClasses = 'ad-loading-placeholder';
  switch (props.adType) {
    case 'banner':
      return `${baseClasses} h-20 w-full`;
    case 'rectangle':
      return `${baseClasses} h-64 w-80 mx-auto`;
    case 'square':
      return `${baseClasses} h-64 w-64 mx-auto`;
    case 'infeed':
      return `${baseClasses} h-32 w-full`;
    default:
      return `${baseClasses} h-20 w-full`;
  }
});

// Methods
const loadAd = async () => {
  if (!shouldShowAd.value || !adContainer.value) {
    return;
  }

  isLoading.value = true;

  // Set a timeout to hide loading state after 3 seconds
  loadTimeout.value = setTimeout(() => {
    isLoading.value = false;
    if (!adLoaded.value) {
      console.log(`Ad load timeout for ${props.adType}`);
      emit('ad-failed');
    }
  }, 3000);

  try {
    await nextTick();
    
    // Check if container has width before loading ad
    const containerWidth = adContainer.value.offsetWidth;
    if (containerWidth === 0) {
      console.log(`Skipping ad load - container width is 0 for ${props.adType}`);
      isLoading.value = false;
      return;
    }
    
    // Ensure adsbygoogle array exists
    if (typeof window !== 'undefined') {
      const adWindow = window as Window & {
        adsbygoogle?: Array<Record<string, unknown>> & { loaded?: boolean };
      };
      const adsbygoogle = adWindow.adsbygoogle || [];

      adWindow.adsbygoogle = adsbygoogle;

      // Push the ad
      adsbygoogle.push({});
      
      // Mark as loaded and clear loading state
      adLoaded.value = true;
      isLoading.value = false;
      
      // Clear timeout since ad loaded successfully
      if (loadTimeout.value) {
        clearTimeout(loadTimeout.value);
        loadTimeout.value = null;
      }
      
      // Emit success event
      emit('ad-loaded');
      
      console.log(`Google Ad loaded: ${props.adType}`);
    }
  } catch (error) {
    console.error('Error loading Google Ad:', error);
    isLoading.value = false;
    emit('ad-failed');
  }
};

// Cleanup function
const cleanup = () => {
  if (loadTimeout.value) {
    clearTimeout(loadTimeout.value);
    loadTimeout.value = null;
  }
};

// Lifecycle
onMounted(async () => {
  if (shouldShowAd.value) {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      loadAd();
    }, 100);
  }
});

// Cleanup on unmount
onUnmounted(() => {
  cleanup();
});
</script>

<style scoped>
.google-ad-container {
  @apply transition-all duration-300;
}

.google-ad-container.ad-loaded {
  @apply opacity-100;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .google-ad-container {
    @apply w-full;
  }
}

/* Ensure ads don't break layout */
.adsbygoogle {
  @apply block w-full;
}
</style>